import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import NumberFormat from "react-number-format";

// Material UI components
import Grid from "@mui/material/Grid";
import Slider from "@mui/material/Slider";
import Input from "@mui/material/Input";
import Button from "@mui/material/Button";

// Styles
import { useStyles } from "./InputSlider.styles";

const NumberFormatCustom = React.forwardRef(({ ...other }, inputRef) => {
  const { onChange, className, thousands, prefix, suffix, name, ...rest } =
    other;
  return (
    <NumberFormat
      {...rest}
      className={className}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name,
            value: values.value,
          },
        });
      }}
      thousandSeparator={thousands}
      isNumericString
      prefix={prefix}
      suffix={suffix}
    />
  );
});

NumberFormatCustom.displayName = "NumberFormatCustom";

const InputSlider = ({
  name,
  min,
  max,
  step,
  values,
  prefix,
  suffix,
  thousands,
  valuesChanged,
  clearButton,
  width,
  disabled = false,
}) => {
  const classes = useStyles({ width });

  const parseIntArray = (inputArray) => [
    parseInt(inputArray[0]),
    parseInt(inputArray[1]),
  ];

  const [sliderValues, setSliderValues] = useState([min, max]);
  const [mouseOutValues, setMouseOutValues] = useState([min, max]);
  const [componentLoaded, setComponentLoaded] = useState(false);

  const defaultToParamValues = () => {
    const emptyFormats = [null, "null", "nil"];
    const defaultingValues = [];
    defaultingValues[0] =
      emptyFormats.includes(values[0]) || Number.isNaN(values[0])
        ? min
        : values[0];
    defaultingValues[1] =
      emptyFormats.includes(values[1]) || Number.isNaN(values[1])
        ? max
        : values[1];

    setSliderValues(parseIntArray(defaultingValues));
  };

  const validateInputs = (inputValues) => {
    const [filterValueMin, filterValueMax] = inputValues;

    if (filterValueMin === "" || filterValueMax === "") {
      return false;
    }

    if (filterValueMin < min || filterValueMax > max) {
      return false;
    }
    // If the values haven't changed dont update
    if (values[0] === filterValueMin && values[1] === filterValueMax) {
      return false;
    }
    // If the values haven't been changed from default dont update
    if (
      values.length === 0 &&
      filterValueMin === min &&
      filterValueMax === max
    ) {
      return false;
    }
    // If max is less than min dont update
    if (filterValueMax < filterValueMin) {
      return false;
    }

    return true;
  };

  const updateInputsIfValid = () => {
    if (validateInputs(sliderValues)) {
      setMouseOutValues(sliderValues);
    } else {
      defaultToParamValues();
    }
  };
  const handleChangeCommitted = (event, funcValues) => {
    if (validateInputs(parseIntArray(funcValues))) {
      setMouseOutValues(parseIntArray(funcValues));
    }
  };

  const handleChange = (event, funcValues) => {
    setSliderValues(parseIntArray(funcValues));
  };

  const handleInputChange = (type, event) => {
    if (type === "min") {
      setSliderValues(parseIntArray([event.target.value, sliderValues[1]]));
    }
    if (type === "max") {
      setSliderValues(parseIntArray([sliderValues[0], event.target.value]));
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      updateInputsIfValid();
    }
  };

  const handleFocusOut = () => {
    updateInputsIfValid();
  };

  useEffect(() => {
    if (values.length === 0) {
      setSliderValues([min, max]);
    } else {
      defaultToParamValues();
    }
  }, [values]);

  useEffect(() => {
    let badValue = false;
    const returningValues = [null, null];
    if (componentLoaded) {
      if (sliderValues[0] > min && !Number.isNaN(sliderValues[0])) {
        // eslint-disable-next-line
        returningValues[0] = sliderValues[0];
      }
      if (sliderValues[1] < max && !Number.isNaN(sliderValues[1])) {
        // eslint-disable-next-line
        returningValues[1] = sliderValues[1];
      }
      if (
        [min, null].includes(returningValues[0]) &&
        [max, null].includes(returningValues[1])
      ) {
        badValue = true;
      }
      valuesChanged(name, returningValues, badValue);
    } else {
      setComponentLoaded(true);
    }
  }, [mouseOutValues]);

  return (
    <div className={classes.root}>
      <Grid container alignItems="center">
        <Grid item xs={12}>
          <Slider
            disabled={disabled}
            value={sliderValues}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            onChangeCommitted={handleChangeCommitted}
            aria-labelledby="input-slider"
            // getAriaLabel={(i) => {return (`input-slider-${i}`)}}
          />
        </Grid>
        <Grid item xs={12} className={classes.flex}>
          <Input
            disabled={disabled}
            className={classes.input}
            value={sliderValues[0]}
            margin="dense"
            onFocus={(event) => {
              event.target.select();
            }}
            onChange={(e) => {
              handleInputChange("min", e);
            }}
            onBlur={handleFocusOut}
            onKeyPress={handleKeyPress}
            inputComponent={NumberFormatCustom}
            inputProps={{
              prefix,
              suffix,
              thousands,
              className: classes.leftInput,
              "data-testid": "left-input",
            }}
            data-cy={`filter-input-low-${name.toLowerCase()}`}
          />
          <div className={classes.grow} />

          <Input
            disabled={disabled}
            className={classes.input}
            value={sliderValues[1]}
            margin="dense"
            onFocus={(event) => {
              event.target.select();
            }}
            onChange={(e) => {
              handleInputChange("max", e);
            }}
            onBlur={handleFocusOut}
            onKeyPress={handleKeyPress}
            inputComponent={NumberFormatCustom}
            inputProps={{
              prefix,
              suffix,
              thousands,
              className: classes.rightInput,
              "data-testid": "right-input",
            }}
            data-cy={`filter-input-high-${name.toLowerCase()}`}
          />
        </Grid>
      </Grid>
      {clearButton && (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => valuesChanged(name, [])}
          className={classes.clearAllFilters}
          data-cy={`clear-range-filters-${name}`}
        >
          Reset
        </Button>
      )}
    </div>
  );
};

InputSlider.propTypes = {
  name: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  values: PropTypes.arrayOf(PropTypes.number),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  thousands: PropTypes.string,
  clearButton: PropTypes.bool,
  valuesChanged: PropTypes.func,
  width: PropTypes.string,
};

InputSlider.defaultProps = {
  name: "default slider",
  min: 0,
  max: 100,
  step: 1,
  values: [0, 1],
  prefix: "",
  suffix: "",
  thousands: null,
  clearButton: false,
  valuesChanged: () => {},
  width: "85%",
};

export default InputSlider;
